import type { ExploreStoryCard } from "../types";

/**
 * Layout slot types for the mobile story feed.
 * Each slot maps to a specific card component.
 */
export type MobileSlot =
  | { variant: "hero"; story: ExploreStoryCard }
  | { variant: "image"; story: ExploreStoryCard }
  | { variant: "headline"; story: ExploreStoryCard }
  | { variant: "full-bleed"; story: ExploreStoryCard }
  | { variant: "text"; story: ExploreStoryCard; showDivider: boolean }
  | { variant: "elevated"; story: ExploreStoryCard }
  | { variant: "compact-pair"; stories: ExploreStoryCard[] }
  | { variant: "grid-quad"; stories: ExploreStoryCard[] }
  | { variant: "text-only"; story: ExploreStoryCard; showDivider: boolean }
  | { variant: "tiny-list"; story: ExploreStoryCard; showDivider: boolean }
  | { variant: "section-divider"; title: string };

/** Supported mobile layout variants (production uses 15). */
export type MobileLayoutVariant =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15;

/**
 * Build layout slots for the Fresh Stories section (mobile).
 *
 * V15 — Option B (flat): text → full-bleed → text → image → text
 */
export function buildFreshStoriesMobileSlots({
  stories,
}: {
  stories: ExploreStoryCard[];
}): MobileSlot[] {
  if (stories.length === 0) {
    return [];
  }

  return buildRhythmFresh({ stories });
}

export function buildTopStoriesMobileSlots({
  stories,
}: {
  stories: ExploreStoryCard[];
}): MobileSlot[] {
  if (stories.length === 0) {
    return [];
  }

  const featured = stories[0];
  const rest = stories.slice(1);

  return [
    { variant: "headline", story: featured },
    ...textSlots({ stories: rest }),
  ];
}

function textSlots({ stories }: { stories: ExploreStoryCard[] }): MobileSlot[] {
  return stories.map((story, idx) => ({
    variant: "text" as const,
    story,
    showDivider: idx < stories.length - 1,
  }));
}

const MIN_SMALL_BETWEEN_BREAKS = 2;

/**
 * V15 rhythm builder: flat text cards with visual breaks at natural intervals.
 *
 * Walks through stories, placing text cards and inserting visual break slots
 * at natural intervals. Ensures minimum gap between large cards. Visual stories
 * (BREAKING/RELEASE) are promoted to the next available break slot.
 */
function buildRhythmFresh({
  stories,
}: {
  stories: ExploreStoryCard[];
}): MobileSlot[] {
  const { visual, regular } = partitionByVisualWeight({ stories });

  const slots: MobileSlot[] = [];
  let regularIdx = 0;
  let visualIdx = 0;
  let smallSinceLastBreak = 0;
  let breakCount = 0;
  const maxBreaks = 3;

  while (regularIdx < regular.length || visualIdx < visual.length) {
    const hasVisual = visualIdx < visual.length;
    const minGap = breakCount === 0 ? 1 : MIN_SMALL_BETWEEN_BREAKS;
    const wantBreak =
      breakCount < maxBreaks && smallSinceLastBreak >= minGap && hasVisual;

    if (wantBreak) {
      slots.push(visualBreakSlot({ story: visual[visualIdx] }));
      visualIdx++;
      smallSinceLastBreak = 0;
      breakCount++;
    } else if (regularIdx < regular.length) {
      slots.push({
        variant: "text",
        story: regular[regularIdx],
        showDivider: true,
      });
      regularIdx++;
      smallSinceLastBreak++;
    } else if (hasVisual) {
      if (
        smallSinceLastBreak >= MIN_SMALL_BETWEEN_BREAKS &&
        breakCount < maxBreaks
      ) {
        slots.push(visualBreakSlot({ story: visual[visualIdx] }));
        breakCount++;
        smallSinceLastBreak = 0;
      } else {
        slots.push({
          variant: "text",
          story: visual[visualIdx],
          showDivider: true,
        });
        smallSinceLastBreak++;
      }
      visualIdx++;
    }
  }

  if (slots.length > 0) {
    const lastSlot = slots.at(-1);
    if (lastSlot && lastSlot.variant === "text") {
      lastSlot.showDivider = false;
    }
  }

  return slots;
}

function visualBreakSlot({ story }: { story: ExploreStoryCard }): MobileSlot {
  const isHeadlineWorthy =
    story.lifecycle === "BREAKING" ||
    story.storyBadge === "release" ||
    story.materialType === "RELEASE";

  return isHeadlineWorthy
    ? { variant: "headline", story }
    : { variant: "image", story };
}

function partitionByVisualWeight({
  stories,
}: {
  stories: ExploreStoryCard[];
}): {
  visual: ExploreStoryCard[];
  regular: ExploreStoryCard[];
} {
  const visual: ExploreStoryCard[] = [];
  const regular: ExploreStoryCard[] = [];

  for (const story of stories) {
    const isVisual =
      story.lifecycle === "BREAKING" ||
      story.storyBadge === "release" ||
      story.materialType === "RELEASE" ||
      story.importance === "HIGH";

    if (isVisual) {
      visual.push(story);
    } else {
      regular.push(story);
    }
  }

  return { visual, regular };
}
